import Copyright from '../components/Copyright';
import React, { useState, useEffect, useCallback } from 'react';
import { logsAPI } from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

const today = () => format(new Date(), 'yyyy-MM-dd');

function RatingRow({ label, value, onChange }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <span style={{ width: 110, fontSize: 14, color: 'var(--text-2)' }}>{label}</span>
      <div className="rating-row">
        {[1,2,3,4,5].map(n => (
          <button key={n} type="button"
            className={`rating-dot ${value >= n ? 'active' : ''}`}
            onClick={() => onChange(value === n ? null : n)}>
            {n}
          </button>
        ))}
      </div>
    </div>
  );
}

function SetRow({ set, setNum, onChange, onRemove, isOnly }) {
  const setField = (k) => (e) => onChange({ ...set, [k]: e.target.value || null });
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
      <div style={{
        minWidth: 28, height: 28, borderRadius: 6, background: 'var(--red)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 12, fontWeight: 700, color: 'white', flexShrink: 0
      }}>
        {setNum}
      </div>
      {[['reps','Reps','number','60px'],['weight','lbs','decimal','70px'],
        ['duration_minutes','min','decimal','60px'],['heart_rate','bpm','number','60px']
      ].map(([k, label, type, width]) => (
        <div key={k} style={{ flex: 1, minWidth: width }}>
          <input className="input" type="number" step={type === 'decimal' ? '0.1' : '1'}
            value={set[k] || ''} onChange={setField(k)}
            placeholder={label}
            style={{ padding: '6px 8px', fontSize: 13, textAlign: 'center' }} />
        </div>
      ))}
      <input className="input" value={set.notes || ''} onChange={setField('notes')}
        placeholder="Notes" style={{ flex: 2, padding: '6px 8px', fontSize: 13 }} />
      {!isOnly && (
        <button type="button" onClick={onRemove}
          style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 14, padding: '0 4px' }}>✕</button>
      )}
    </div>
  );
}

function ExerciseRow({ exercise, index, onChange, onRemove }) {
  const setsData = exercise.sets_data?.length > 0
    ? exercise.sets_data
    : [{ reps: exercise.reps || null, weight: exercise.weight || null,
         duration_minutes: exercise.duration_minutes || null,
         heart_rate: exercise.heart_rate || null, notes: null }];

  const updateSets = (newSets) => onChange({ ...exercise, sets_data: newSets });

  const addSet = () => {
    const last = setsData[setsData.length - 1];
    updateSets([...setsData, { ...last, notes: null }]);
  };

  const removeSet = (i) => updateSets(setsData.filter((_, j) => j !== i));

  const updateSet = (i, updated) => updateSets(setsData.map((s, j) => j === i ? updated : s));

  return (
    <div style={{ background: 'var(--bg-3)', borderRadius: 'var(--radius)', padding: 14, marginBottom: 10 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--red)' }}>Exercise {index + 1}</span>
        <button type="button" onClick={onRemove}
          style={{ background: 'none', border: 'none', color: 'var(--text-3)', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>
      <input className="input" value={exercise.exercise_name || ''} onChange={e => onChange({ ...exercise, exercise_name: e.target.value })}
        placeholder="Exercise name" style={{ marginBottom: 10 }} />

      {/* Set column headers */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 4, paddingLeft: 34 }}>
        {['Reps', 'Weight', 'Time', 'HR', 'Notes'].map(h => (
          <div key={h} style={{ flex: h === 'Notes' ? 2 : 1, fontSize: 10, fontWeight: 600,
            color: 'var(--text-3)', textAlign: 'center', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {h}
          </div>
        ))}
        <div style={{ width: 22 }} />
      </div>

      {/* Individual sets */}
      {setsData.map((set, i) => (
        <SetRow key={i} set={set} setNum={i + 1}
          onChange={(updated) => updateSet(i, updated)}
          onRemove={() => removeSet(i)}
          isOnly={setsData.length === 1} />
      ))}

      <button type="button" onClick={addSet}
        className="btn btn-secondary btn-sm"
        style={{ marginTop: 6, fontSize: 12 }}>
        + Add Set {setsData.length + 1}
      </button>
    </div>
  );
}

export default function TodayPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState(() => searchParams.get('date') || today());
  const [log, setLog] = useState({});
  const [exercises, setExercises] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const loadLog = useCallback(async (d) => {
    setLoading(true);
    try {
      const res = await logsAPI.getLogByDate(d);
      const data = res.data;
      setLog(data);
      setExercises(data.exercises || []);
    } catch (err) {
      if (err.response?.status === 404) {
        setLog({ log_date: d });
        setExercises([]);
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadLog(date); }, [date, loadLog]);

  const set = (k) => (v) => setLog(p => ({ ...p, [k]: v }));
  const setField = (k) => (e) => setLog(p => ({ ...p, [k]: e.target.value || null }));

  // Auto-calc BMI
  useEffect(() => {
    if (log.weight && user?.height && !log.bmi_override) {
      const hm = user.measurement_scale === 'imperial' ? user.height * 0.0254 : user.height / 100;
      const wkg = user.measurement_scale === 'imperial' ? log.weight * 0.453592 : log.weight;
      const bmi = (wkg / (hm * hm)).toFixed(1);
      setLog(p => ({ ...p, bmi }));
    }
  }, [log.weight, user, log.bmi_override]);

  const macroCalories = ((parseFloat(log.protein_g)||0)*4 + (parseFloat(log.carbs_g)||0)*4 + (parseFloat(log.fat_g)||0)*9).toFixed(0);

  const save = async () => {
    setSaving(true);
    try {
      await logsAPI.saveLog({ ...log, log_date: date, exercises });
      toast.success('Log saved!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Save failed');
    } finally { setSaving(false); }
  };

  const addExercise = () => setExercises(p => [...p, { exercise_name: '', sets_data: [{}] }]);

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">DAILY LOG</h1>
          <p style={{ color: 'var(--text-2)', fontSize: 14 }}>
            {date === today() ? "Track today's wellness data" : `Editing log for ${date}`}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <input type="date" className="input" value={date}
            onChange={e => {
              setDate(e.target.value);
              setSearchParams(e.target.value !== today() ? { date: e.target.value } : {});
            }}
            style={{ width: 'auto' }} />
          {date !== today() && (
            <button className="btn btn-ghost btn-sm" onClick={() => {
              setDate(today());
              setSearchParams({});
            }}>← Today</button>
          )}
          <button className="btn btn-primary" onClick={save} disabled={saving}>
            {saving ? '⏳ Saving...' : '💾 Save Log'}
          </button>
        </div>
      </div>

      <div className="page-body" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>

        {/* Left Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Body Metrics */}
          <div className="card">
            <div className="section-title">⚖️ Body Metrics</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="input-row">
                <div className="input-group">
                  <label className="input-label">Weight ({user?.measurement_scale === 'imperial' ? 'lbs' : 'kg'})</label>
                  <input className="input" type="number" step="0.1" value={log.weight || ''}
                    onChange={e => setLog(p => ({ ...p, weight: e.target.value || null }))} />
                </div>
                <div className="input-group">
                  <label className="input-label">
                    BMI {!log.bmi_override && log.bmi && <span style={{ color: 'var(--red)', fontSize: 11 }}> auto</span>}
                  </label>
                  <input className="input" type="number" step="0.1" value={log.bmi || ''}
                    onChange={e => setLog(p => ({ ...p, bmi: e.target.value || null, bmi_override: !!e.target.value }))} />
                </div>
              </div>
              <div className="input-row">
                <div className="input-group">
                  <label className="input-label">Calories (kcal)</label>
                  <input className="input" type="number" value={log.caloric_intake || ''} onChange={setField('caloric_intake')} />
                </div>
                <div className="input-group">
                  <label className="input-label">Fasting Blood Sugar</label>
                  <input className="input" type="number" step="0.1" value={log.fasting_blood_sugar || ''} onChange={setField('fasting_blood_sugar')} />
                </div>
              </div>
              <div className="input-group">
                <label className="input-label">Water Intake (oz)</label>
                <input className="input" type="number" step="0.1" value={log.water_intake || ''} onChange={setField('water_intake')} />
              </div>
            </div>
          </div>

          {/* Macros */}
          <div className="card">
            <div className="section-title">🥗 Macros</div>
            {parseFloat(macroCalories) > 0 && (
              <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
                {[
                  { label: 'Protein', value: log.protein_g, unit: 'g', color: 'var(--blue)' },
                  { label: 'Carbs', value: log.carbs_g, unit: 'g', color: 'var(--amber)' },
                  { label: 'Fat', value: log.fat_g, unit: 'g', color: 'var(--red)' },
                  { label: 'Cals', value: macroCalories, unit: 'kcal', color: 'var(--green)' },
                ].map(({ label, value, unit, color }) => (
                  <div key={label} className="macro-pill" style={{ borderColor: `${color}40` }}>
                    <span className="macro-pill-value" style={{ color }}>{value ? Math.round(value) : '--'}</span>
                    <span className="macro-pill-label">{label} {unit}</span>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div className="input-row-3">
                {[['protein_g','Protein (g)'],['carbs_g','Carbs (g)'],['fat_g','Fat (g)']].map(([k,l]) => (
                  <div key={k} className="input-group">
                    <label className="input-label">{l}</label>
                    <input className="input" type="number" step="0.1" value={log[k] || ''} onChange={setField(k)} />
                  </div>
                ))}
              </div>
              <div className="input-row-3">
                {[['fiber_g','Fiber (g)'],['sugar_g','Sugar (g)'],['sodium_mg','Sodium (mg)']].map(([k,l]) => (
                  <div key={k} className="input-group">
                    <label className="input-label">{l}</label>
                    <input className="input" type="number" step="0.1" value={log[k] || ''} onChange={setField(k)} />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Measurements */}
          <div className="card">
            <div className="section-title">📏 Body Measurements</div>
            <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 12 }}>Biceps</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2,1fr)', gap: 10, marginBottom: 12 }}>
              {[['left_bicep_flexed','Left Flexed'],['left_bicep_unflexed','Left Unflexed'],
                ['right_bicep_flexed','Right Flexed'],['right_bicep_unflexed','Right Unflexed']].map(([k,l]) => (
                <div key={k} className="input-group">
                  <label className="input-label">{l}</label>
                  <input className="input" type="number" step="0.01" value={log[k] || ''} onChange={setField(k)} />
                </div>
              ))}
            </div>
            <div className="divider" />
            <p style={{ fontSize: 13, color: 'var(--text-3)', marginBottom: 12 }}>Thighs & Stomach</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 10 }}>
              {[['left_thigh','Left Thigh'],['right_thigh','Right Thigh'],['stomach_measurement','Stomach (exhaled)']].map(([k,l]) => (
                <div key={k} className="input-group">
                  <label className="input-label">{l}</label>
                  <input className="input" type="number" step="0.01" value={log[k] || ''} onChange={setField(k)} />
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Exercises */}
          <div className="card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
              <div className="section-title" style={{ margin: 0 }}>💪 Exercises <span className="badge badge-red">{exercises.length}</span></div>
              <button className="btn btn-secondary btn-sm" onClick={addExercise}>+ Add</button>
            </div>
            {exercises.length === 0 ? (
              <div className="empty-state" style={{ padding: 24 }}>
                <div className="empty-state-icon">🏋️</div>
                <p className="empty-state-sub">No exercises yet. Tap + to add one.</p>
              </div>
            ) : exercises.map((ex, i) => (
              <ExerciseRow key={i} exercise={ex} index={i}
                onChange={(updated) => setExercises(p => p.map((e, j) => j === i ? updated : e))}
                onRemove={() => setExercises(p => p.filter((_, j) => j !== i))} />
            ))}
          </div>

          {/* Wellness & Mood */}
          <div className="card">
            <div className="section-title">🌟 Wellness & Mood</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="input-group">
                <label className="input-label">Compliments Today</label>
                <textarea className="input" rows={3} value={log.compliments || ''}
                  onChange={setField('compliments')} placeholder="Something positive about today..." />
              </div>
              <div className="input-group">
                <label className="input-label">How do you feel?</label>
                <textarea className="input" rows={3} value={log.how_feel || ''}
                  onChange={setField('how_feel')} placeholder="How are you feeling today..." />
              </div>
            </div>
          </div>

          {/* Day Ratings */}
          <div className="card">
            <div className="section-title">⭐ Rate Your Day</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <RatingRow label="Morning" value={log.rating_morning}
                onChange={(v) => setLog(p => ({ ...p, rating_morning: v }))} />
              <RatingRow label="Afternoon" value={log.rating_afternoon}
                onChange={(v) => setLog(p => ({ ...p, rating_afternoon: v }))} />
              <RatingRow label="Evening" value={log.rating_evening}
                onChange={(v) => setLog(p => ({ ...p, rating_evening: v }))} />
              <div className="divider" />
              <div className="input-row">
                <div className="input-group">
                  <label className="input-label">Sleep Hours</label>
                  <input className="input" type="number" step="0.5" min="0" max="24"
                    value={log.hours_sleep || ''} onChange={setField('hours_sleep')} />
                </div>
                <div className="input-group">
                  <label className="input-label">Sleep Rating</label>
                  <div className="rating-row" style={{ marginTop: 8 }}>
                    {[1,2,3,4,5].map(n => (
                      <button key={n} type="button"
                        className={`rating-dot ${log.rating_sleep >= n ? 'active' : ''}`}
                        onClick={() => setLog(p => ({ ...p, rating_sleep: p.rating_sleep === n ? null : n }))}>
                        {n}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Save button */}
          <button className="btn btn-primary btn-full btn-lg" onClick={save} disabled={saving}>
            {saving ? '⏳ Saving...' : '💾 Save Log'}
          </button>
        </div>
      </div>
    <Copyright />
    </div>
  );
}
